import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation, i18n } from 'next-i18next';
import { useAuth } from '@lb-classroom/feature-auth';
import LessonIssueApiUtils from '@utils/api/lessonIssue';
import MomentFormatter from '@utils/MomentFormatter';
import appUtils from '@utils/appUtils';
import type {
  ResolvedIssueModalContainerProps,
  LessonIssue,
} from './resolved-issue-modal.types';

export const useResolvedIssueModalService = (
  props: ResolvedIssueModalContainerProps,
) => {
  const { hideModal, lesson } = props;
  const { t } = useTranslation('common');
  const { currentUser } = useAuth();

  const [issue, setIssue] = useState<LessonIssue | null>(
    lesson.lesson_issue || null,
  );
  const [isLoading, setIsLoading] = useState(!lesson.lesson_issue);

  useEffect(() => {
    const fetchIssue = async () => {
      if (!issue) {
        try {
          const result = await LessonIssueApiUtils.fetchOne(lesson.id);
          if (result) {
            setIssue(result);
          }
        } catch {
          // Error fetching issue
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchIssue();
  }, [issue, lesson.id]);

  const formatter = useMemo(() => {
    const currentLocale = appUtils.getLocaleFromLang(i18n?.language || 'en');
    const timeZone = currentUser?.js_time_zone || 'UTC';
    return new MomentFormatter(currentLocale, timeZone);
  }, [currentUser?.js_time_zone]);

  const formattedData = useMemo(() => {
    const startFormat = formatter.lessonFormat(lesson.start_at);
    const endFormat = formatter.lessonFormat(lesson.end_at);
    const noData = t('general.noData');

    let peerProfile: string;
    let peerName: string;
    if (currentUser?.profile_type === 'Student') {
      peerName = lesson.tutor_name;
      peerProfile = t('general.tutor');
    } else {
      peerName = lesson.student_name;
      peerProfile = t('general.student');
    }

    let issueType = t('general.loading');
    let reasonTitle = t('general.loading');
    let actionTitle = t('general.loading');

    if (issue) {
      issueType = issue.issue_types[0];
      reasonTitle = issue.reason?.title || '';
      actionTitle = issue.action?.title || '';
    }

    const consumedTickets =
      lesson.consumed_tickets !== null
        ? t('units.mai', {
            count: +lesson.consumed_tickets,
            smart_count: +lesson.consumed_tickets,
          })
        : noData;
    const consumedNoExpirationTickets =
      lesson.consumed_sub_tickets !== null
        ? t('units.mai', {
            count: +lesson.consumed_sub_tickets,
            smart_count: +lesson.consumed_sub_tickets,
          })
        : noData;
    const returnedTickets =
      lesson.returned_tickets !== null
        ? t('units.mai', {
            count: +lesson.returned_tickets,
            smart_count: +lesson.returned_tickets,
          })
        : noData;
    const returnedNoExpirationTickets =
      lesson.returned_sub_tickets !== null
        ? t('units.mai', {
            count: +lesson.returned_sub_tickets,
            smart_count: +lesson.returned_sub_tickets,
          })
        : noData;

    return {
      peerProfile,
      peerName,
      lessonTime: `${startFormat.formattedTime} - ${endFormat.formattedTime}`,
      lessonDate: startFormat.formattedDate,
      consumedTickets,
      consumedNoExpirationTickets,
      issueType,
      reasonTitle,
      actionTitle,
      returnedTickets,
      returnedNoExpirationTickets,
    };
  }, [formatter, lesson, issue, currentUser?.profile_type, t]);

  const handleClose = useCallback(() => {
    hideModal();
  }, [hideModal]);

  return {
    ...formattedData,
    isLoading,
    onClose: handleClose,
  };
};
